import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  draft: { label: 'Brouillon', color: 'bg-gray-200 text-gray-700' },
  generating: { label: 'Generation IA...', color: 'bg-blue-100 text-blue-700 animate-pulse' },
  ready: { label: 'A relire', color: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Programmee', color: 'bg-teal-100 text-teal-800' },
  sending: { label: 'Envoi en cours', color: 'bg-purple-100 text-purple-700 animate-pulse' },
  sent: { label: 'Envoyee', color: 'bg-green-100 text-green-700' },
  archived: { label: 'Archivee', color: 'bg-gray-100 text-gray-500' },
};

const BRANCH_LABEL = (b) => (b === 'nord' ? 'RealEase Nord' : 'RealEase Bretagne');

function StatusPill({ status }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.draft;
  return (
    <span data-testid="newsletter-status-pill" className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.color}`}>
      {s.label}
    </span>
  );
}

function CreateCampaignModal({ onClose, onCreated, branch, canPickBranch }) {
  const [theme, setTheme] = useState('');
  const [directives, setDirectives] = useState('');
  const [tone, setTone] = useState('professionnel mais chaleureux');
  const [selectedBranch, setSelectedBranch] = useState(branch);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/campaigns`, {
        theme: theme || null,
        directives: directives || null,
        tone,
        branch: canPickBranch ? selectedBranch : undefined,
      });
      toast.success('Campagne creee. Generation IA en cours...');
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur creation campagne');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="create-newsletter-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#003E40]">Nouvelle campagne newsletter</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-newsletter-modal">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="p-6 space-y-4">
          {canPickBranch && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branche</label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D]"
                data-testid="newsletter-branch-select"
              >
                <option value="bretagne">RealEase Bretagne</option>
                <option value="nord">RealEase Nord</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Theme du mois (optionnel)</label>
            <input
              type="text"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="Ex: Marche immobilier breton, hivernal"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D]"
              data-testid="newsletter-theme-input"
            />
            <p className="text-xs text-gray-500 mt-1">Laissez vide pour que l'IA choisisse selon la saison.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Directives specifiques (optionnel)</label>
            <textarea
              value={directives}
              onChange={(e) => setDirectives(e.target.value)}
              rows={4}
              placeholder="Ex: Mettre en avant un projet recent a Quimper. Ne pas parler de Nantes."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D]"
              data-testid="newsletter-directives-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ton</label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              data-testid="newsletter-tone-select"
            >
              <option value="professionnel mais chaleureux">Professionnel mais chaleureux (defaut)</option>
              <option value="direct et factuel">Direct et factuel</option>
              <option value="experte et pedagogique">Experte et pedagogique</option>
              <option value="convivial et accessible">Convivial et accessible</option>
            </select>
          </div>
        </div>
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-200" data-testid="cancel-create-newsletter">
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-[#008A8D] text-white hover:bg-[#003E40] disabled:opacity-50 font-medium"
            data-testid="submit-create-newsletter"
          >
            {saving ? 'Creation...' : 'Creer et generer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CampaignEditor({ campaign, onClose, onUpdated }) {
  const [subject, setSubject] = useState(campaign.subject || '');
  const [bodyHtml, setBodyHtml] = useState(campaign.body_html || '');
  const [previewText, setPreviewText] = useState(campaign.preview_text || '');
  const [saving, setSaving] = useState(false);
  const [polling, setPolling] = useState(campaign.status === 'generating');
  const iframeRef = useRef(null);

  // Polling pour suivre l'etat de generation IA
  useEffect(() => {
    if (campaign.status !== 'generating') {
      setPolling(false);
      return;
    }
    setPolling(true);
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/newsletter/campaigns/${campaign.campaign_id}`);
        if (data.status !== 'generating') {
          setSubject(data.subject || '');
          setBodyHtml(data.body_html || '');
          setPreviewText(data.preview_text || '');
          setPolling(false);
          onUpdated(data);
          clearInterval(interval);
          toast.success('Newsletter generee par Claude !');
        }
      } catch {
        // continue polling
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [campaign.campaign_id, campaign.status, onUpdated]);

  // Re-injecte le HTML dans l'iframe a chaque modif
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const personalized = (bodyHtml || '').replace(/\{\{first_name\}\}/g, 'Marie').replace(/\{\{last_name\}\}/g, 'Dupont');
    const wrapped = `<!doctype html><html><head><meta charset="utf-8"/><base target="_blank"/><style>body{margin:0;padding:24px;font-family:Georgia,serif;line-height:1.7;background:#f9fafb;color:#1a1a1a;}</style></head><body>${personalized}</body></html>`;
    iframe.srcdoc = wrapped;
  }, [bodyHtml]);

  const saveChanges = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/newsletter/campaigns/${campaign.campaign_id}`, {
        subject,
        body_html: bodyHtml,
        preview_text: previewText,
      });
      onUpdated(data);
      toast.success('Modifications enregistrees');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Regenerer entierement la newsletter par IA ? Le contenu actuel sera remplace.')) return;
    try {
      await axios.post(`${API}/newsletter/campaigns/${campaign.campaign_id}/regenerate`);
      toast.info('Regeneration lancee...');
      setPolling(true);
      onUpdated({ ...campaign, status: 'generating' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur regeneration');
    }
  };

  const approve = async () => {
    if (!window.confirm('Programmer cette campagne pour envoi automatique au 1er du mois suivant ?')) return;
    try {
      const { data } = await axios.post(`${API}/newsletter/campaigns/${campaign.campaign_id}/approve`, {});
      onUpdated({ ...campaign, status: 'approved', scheduled_at: data.scheduled_at });
      toast.success(`Campagne programmee pour ${new Date(data.scheduled_at).toLocaleDateString('fr-FR')}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur approbation');
    }
  };

  const sendNow = async () => {
    if (!window.confirm(`Envoyer maintenant a ${campaign.audience_count} destinataires ? Action irreversible.`)) return;
    try {
      const { data } = await axios.post(`${API}/newsletter/campaigns/${campaign.campaign_id}/send-now`);
      toast.success(`Envoyee a ${data.sent_count} destinataires !`);
      onUpdated({ ...campaign, status: 'sent', sent_count: data.sent_count });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    }
  };

  const isLocked = ['sending', 'sent', 'archived'].includes(campaign.status);
  const isReady = ['ready', 'draft'].includes(campaign.status) && bodyHtml && subject;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-2xl max-w-7xl w-full h-[95vh] flex flex-col" data-testid="newsletter-editor">
        <div className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-[#003E40]">Newsletter - {BRANCH_LABEL(campaign.branch)}</h2>
            <StatusPill status={campaign.status} />
            <span className="text-sm text-gray-500">{campaign.audience_count || 0} destinataires</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-editor">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {polling && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-3 text-sm text-blue-800 flex items-center gap-2">
            <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Claude redige votre newsletter... (45 secondes en moyenne)
          </div>
        )}

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-0 overflow-hidden">
          {/* Editor */}
          <div className="border-r overflow-y-auto p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Sujet (objet de l'email)</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={isLocked}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#008A8D] focus:border-[#008A8D] font-medium"
                data-testid="newsletter-subject-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Texte de previsualisation (preheader)</label>
              <input
                type="text"
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                disabled={isLocked}
                placeholder="S'affiche apres l'objet dans la boite mail"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#008A8D]"
                data-testid="newsletter-preview-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Corps HTML</label>
              <textarea
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                disabled={isLocked}
                rows={20}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-xs focus:ring-2 focus:ring-[#008A8D]"
                data-testid="newsletter-body-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Utilisez <code className="bg-gray-100 px-1 rounded">{'{{first_name}}'}</code> pour personnaliser (preview = "Marie").
              </p>
            </div>
            {campaign.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                <strong>Erreur IA :</strong> {campaign.error}
              </div>
            )}
          </div>

          {/* Preview iframe */}
          <div className="flex flex-col bg-gray-100 overflow-hidden">
            <div className="px-4 py-2 bg-gray-200 text-xs font-semibold text-gray-600 flex items-center justify-between">
              <span>APERCU DE L'EMAIL</span>
              <span className="text-gray-500">{subject || '(sujet vide)'}</span>
            </div>
            <iframe
              ref={iframeRef}
              title="newsletter-preview"
              className="flex-1 w-full border-0 bg-white"
              sandbox="allow-same-origin"
              data-testid="newsletter-preview-iframe"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={regenerate}
              disabled={isLocked || polling}
              className="px-4 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
              data-testid="regenerate-ai-btn"
            >
              Regenerer par IA
            </button>
            {!isLocked && (
              <button
                onClick={saveChanges}
                disabled={saving || polling}
                className="px-4 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
                data-testid="save-newsletter-btn"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer les modifs'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {campaign.status === 'approved' && campaign.scheduled_at && (
              <span className="text-sm text-teal-700">
                Programmee pour le {new Date(campaign.scheduled_at).toLocaleString('fr-FR')}
              </span>
            )}
            {isReady && campaign.status !== 'approved' && (
              <button
                onClick={approve}
                className="px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm font-semibold"
                data-testid="approve-newsletter-btn"
              >
                Programmer (1er du mois)
              </button>
            )}
            {(isReady || campaign.status === 'approved') && (
              <button
                onClick={sendNow}
                className="px-5 py-2 rounded-lg bg-[#008A8D] text-white hover:bg-[#003E40] text-sm font-semibold"
                data-testid="send-now-btn"
              >
                Envoyer maintenant
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CampaignStatsModal({ campaign, onClose }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/newsletter/stats/${campaign.campaign_id}`)
      .then(({ data }) => setStats(data))
      .catch(() => toast.error('Erreur chargement stats'));
  }, [campaign.campaign_id]);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" data-testid="newsletter-stats-modal">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#003E40]">Statistiques - {campaign.subject}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        {!stats ? (
          <div className="p-12 text-center text-gray-500">Chargement...</div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-[#008A8D]">{stats.sent}</div>
                <div className="text-xs uppercase tracking-wide text-gray-600 mt-1">Envoyes</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-amber-700">{stats.opened}</div>
                <div className="text-xs uppercase tracking-wide text-gray-600 mt-1">Ouvertures</div>
                <div className="text-xs text-amber-600 font-medium">{stats.open_rate}%</div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-purple-700">{stats.clicked}</div>
                <div className="text-xs uppercase tracking-wide text-gray-600 mt-1">Clics</div>
                <div className="text-xs text-purple-600 font-medium">{stats.click_rate}%</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-gray-700">{stats.unsubscribed}</div>
                <div className="text-xs uppercase tracking-wide text-gray-600 mt-1">Desinscriptions</div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Detail par destinataire</h3>
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left sticky top-0">
                    <tr>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Statut</th>
                      <th className="px-3 py-2">Ouvert</th>
                      <th className="px-3 py-2">Clique</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.sends.map((s) => (
                      <tr key={s.send_id} className="border-t">
                        <td className="px-3 py-2 text-gray-700">{s.recipient_email}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            s.status === 'sent' ? 'bg-green-100 text-green-700' :
                            s.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                          }`}>{s.status}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-600">{s.opened_at ? new Date(s.opened_at).toLocaleString('fr-FR') : '-'}</td>
                        <td className="px-3 py-2 text-gray-600">{s.click_count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function NewsletterPage() {
  const { user } = useAuth();
  const canPickBranch = !!user?.cross_branch_access;
  const defaultBranch = user?.branch || 'bretagne';

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState(canPickBranch ? '' : defaultBranch);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statsFor, setStatsFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = branchFilter ? { branch: branchFilter } : {};
      const { data } = await axios.get(`${API}/newsletter/campaigns`, { params });
      setCampaigns(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement campagnes');
    } finally {
      setLoading(false);
    }
  }, [branchFilter]);

  useEffect(() => { load(); }, [load]);

  const removeCampaign = async (id) => {
    if (!window.confirm('Supprimer cette campagne ? Action irreversible.')) return;
    try {
      await axios.delete(`${API}/newsletter/campaigns/${id}`);
      toast.success('Campagne supprimee');
      setCampaigns((cs) => cs.filter((c) => c.campaign_id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="newsletter-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#003E40]">Newsletter mensuelle</h1>
          <p className="text-gray-600 text-sm mt-1">
            Generation IA Claude + envoi via Resend. Audience : tous les prospects par branche.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-5 py-2.5 rounded-xl bg-[#008A8D] text-white hover:bg-[#003E40] font-semibold flex items-center gap-2"
          data-testid="new-campaign-btn"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Nouvelle campagne
        </button>
      </div>

      {canPickBranch && (
        <div className="mb-6 flex items-center gap-2" data-testid="newsletter-branch-filter">
          <span className="text-sm text-gray-500">Filtrer :</span>
          {[
            { v: '', l: 'Toutes les branches' },
            { v: 'bretagne', l: 'Bretagne' },
            { v: 'nord', l: 'Nord' },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setBranchFilter(opt.v)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                branchFilter === opt.v
                  ? 'bg-[#008A8D] text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`newsletter-branch-${opt.v || 'all'}`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Chargement...</div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          <h3 className="font-semibold text-gray-800 mb-1">Aucune campagne pour l'instant</h3>
          <p className="text-sm text-gray-500">Cliquez sur "Nouvelle campagne" pour creer votre 1ere newsletter avec Claude.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="newsletter-list">
          <table className="w-full">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-4 py-3">Mois / Sujet</th>
                <th className="px-4 py-3">Branche</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Audience</th>
                <th className="px-4 py-3 text-right">Envoyes</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.campaign_id} className="border-t hover:bg-gray-50" data-testid={`newsletter-row-${c.campaign_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{c.subject || c.theme || '(sans sujet)'}</div>
                    <div className="text-xs text-gray-500">{c.target_month} • cree le {new Date(c.created_at).toLocaleDateString('fr-FR')}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{BRANCH_LABEL(c.branch)}</td>
                  <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">{c.audience_count || 0}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">{c.sent_count || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {c.status === 'sent' ? (
                        <button onClick={() => setStatsFor(c)} className="text-xs px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 font-medium" data-testid={`stats-btn-${c.campaign_id}`}>
                          Stats
                        </button>
                      ) : (
                        <button onClick={() => setEditing(c)} className="text-xs px-3 py-1.5 rounded-lg bg-[#008A8D] text-white hover:bg-[#003E40] font-medium" data-testid={`edit-btn-${c.campaign_id}`}>
                          Editer
                        </button>
                      )}
                      <button onClick={() => removeCampaign(c.campaign_id)} className="text-xs px-2 py-1.5 rounded-lg text-red-600 hover:bg-red-50" data-testid={`delete-btn-${c.campaign_id}`} title="Supprimer">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateCampaignModal
          onClose={() => setShowCreate(false)}
          onCreated={(c) => { setShowCreate(false); setCampaigns((cs) => [c, ...cs]); setEditing(c); }}
          branch={defaultBranch}
          canPickBranch={canPickBranch}
        />
      )}
      {editing && (
        <CampaignEditor
          campaign={editing}
          onClose={() => { setEditing(null); load(); }}
          onUpdated={(c) => {
            setEditing(c);
            setCampaigns((cs) => cs.map((x) => x.campaign_id === c.campaign_id ? c : x));
          }}
        />
      )}
      {statsFor && <CampaignStatsModal campaign={statsFor} onClose={() => setStatsFor(null)} />}
    </div>
  );
}
